import { useEffect, useMemo, useState } from 'react'
import styles from './importCustomerTypePanel.module.css'
import { CsvImportResult, ImportCustomerTypeField } from '../../../../../features/importer'
import { Action, Badge, Button, FieldList, FieldType, FieldValue, FileField, FileType, FormField, Modal, ModalId, O, option, PanelLoader, useGetCrumbs, useUIActions } from '../../../../../features/ui'
import { RoutePath, useRoutesHooks } from '../../../../../features/routes'
import { useNavigate, useParams } from 'react-router-dom'
import { CustomerTypesCsvSample } from './customerTypesCsvSample'
import { toList } from '../../../../../utils/listUtils'
import { useActions } from './actions'
import { ImportedCustomerTypeReport } from './importedCustomerTypeReport'
import { useDownloadCustomerTypeTemplate } from './useDownloadCustomerTypeTemplate'
import { useCustomerTypeLoaders } from '../../..'
import { CustomerType } from '../../../state/types'

export const ImportCustomerTypePanel = () => {
    const priceFieldOptions = useMemo(() => [
        option(ImportCustomerTypeField.PRICE, 'Precio de Venta'),
        option(ImportCustomerTypeField.PRICE_VAT, 'Precio de Venta c/IVA')
    ], [])

    const { id: existingId } = useParams<{ id: string }>()

    const [step, setStep] = useState(1)
    const [priceFieldValue, setPriceFieldValue] = useState<O<ImportCustomerTypeField>>()
    const [baseCustomerTypeId, setBaseCustomerTypeId] = useState<O<string>>()
    const [currentCustomerType, setCurrentCustomerType] = useState<CustomerType | undefined>();
    const [importFile, setImportFile] = useState<File>()
    const [result, setResult] = useState<O<CsvImportResult>>()
    const [pendingImportData, setPendingImportData] = useState<{result: any, duplicatedId: string, name: string} | null>(null);
    const [allCustomerTypes, setAllCustomerTypes] = useState<CustomerType[]>([])
    const [pendingRenameData, setPendingRenameData] = useState<CustomerType | null>(null);

    const downloadTemplate = useDownloadCustomerTypeTemplate()

    const { findAllCustomerTypes } = useCustomerTypeLoaders()()
    const baseListOptions = useMemo(() => [
        option('none', 'Sin Lista Base'),
        ...allCustomerTypes.map(ct => option(ct.id, ct.name)),
    ], [allCustomerTypes])

    const { getRoutePath } = useRoutesHooks()
    const navigate = useNavigate()
    const { openModal, closeModal } = useUIActions()

    const { processImport, checkNameExists, persist } = useActions();

    const goToCustomerTypes = () => navigate(getRoutePath(RoutePath.CUSTOMER_TYPES))

    const actions: Action[] = useMemo(() => [
        { label: 'Listas de Precios', kind: 'tertiary', onClick: goToCustomerTypes }
    ], [])

    const crumbs = useGetCrumbs(
       'Listas de Precios',
        RoutePath.CUSTOMER_TYPES,
        undefined, undefined,
        'Importar'
    )

    useEffect(() => {
        const loadData = async () => {
            const data = await findAllCustomerTypes();
            setAllCustomerTypes(data);

            if (existingId) {
                const ct = data.find(item => item.id === existingId);
                if (ct) {
                    setCurrentCustomerType(ct);
                    setBaseCustomerTypeId(ct.baseCustomerTypeId);
                    setPriceFieldValue(undefined);
                    setStep(2);
                }
            }
        };
        loadData();
    }, [existingId]);

    const goToStep = (newStep: number) => () => setStep(newStep)

    const renderStep = (
        n: number,
        description: string,
        children: JSX.Element
    ) => {
        const showPreviousBtn = n === step && n > 1 && n < 6
        const showNextBtn = n === step && n > 0 && n < 5 && (
             n === 1 ? true :
             n === 2 ? !!priceFieldValue :
             n === 3 ? true :
             n === 4 ? !!importFile : false
        )

        return (
            <div className={styles.stepContainer}>
                <div className={styles.stepHeader}>
                    <div className={styles.stepDescription}>
                        <Badge
                            label={`Paso ${n}`}
                            kind={n === step ? 'secondary' : 'primary'}
                            icon='arrow-right'
                        />
                        <span>{description}</span>
                    </div>
                    <div className={styles.stepArrows}>
                        {showPreviousBtn && (
                            <Button
                                icon="arrow-up"
                                kind="tertiary"
                                small
                                onClick={goToStep(n - 1)}
                            />
                        )}
                        {showNextBtn && (
                            <Button
                                icon="arrow-down"
                                kind="tertiary"
                                small
                                onClick={goToStep(n + 1)}
                            />
                        )}
                    </div>
                </div>
                {children}
            </div>
        )
    }

    const onChangePriceField = (value?: FieldValue) => {
        const newPriceFieldValue = value as O<ImportCustomerTypeField>
        setPriceFieldValue(newPriceFieldValue)
        newPriceFieldValue && setStep(3)
    }

    const priceFields = [
        {
            name: 'price-field',
            type: FieldType.SELECT,
            options: priceFieldOptions,
            value: priceFieldValue,
            narrow: true,
            onChange: onChangePriceField
        }
    ]

    const onChangeBaseList = (value?: FieldValue) => {
        const val = value as string
        const newBaseListId = val === 'none' ? undefined : val
        setBaseCustomerTypeId(newBaseListId)
        setStep(2)
    }

    const baseListField = [
        {
            name: 'base-list-field',
            type: FieldType.SELECT,
            options: baseListOptions,
            value: baseCustomerTypeId,
            disabled: step !== 1,
            optional: true,
            narrow: true,
            onChange: onChangeBaseList,
        }
    ]

    const onDownloadTemplate = async () => {
        await downloadTemplate(priceFieldValue!, baseCustomerTypeId, currentCustomerType)
        setStep(4)
    }

    const templateField: FormField = {
        name: 'template',
        type: FieldType.FILE,
        render: () => (
            <div className={styles.templateField}>
                <CustomerTypesCsvSample priceField={priceFieldValue!} />
                <Button
                    icon="download"
                    label="Descargar"
                    disabled={step !== 3}
                    onClick={onDownloadTemplate}
                />
            </div>
        )
    }

    const onChangeImportFile = (value?: File) => {
        setImportFile(value)
        value && setStep(5)
    }

    const importField: FormField = {
        name: 'import',
        type: FieldType.FILE,
        label: ' ',
        labelSpace: 2,
        hint: 'Formato: CSV, XLS, XLSX',
        render: () => (
            <FileField
                id="import"
                fileTypes={[FileType.CSV, FileType.XLS, FileType.XLSX]}
                disabled={step !== 4}
                onChange={onChangeImportFile}
            />
        )
    }

    const onCreateCustomerType = async () => {
        const importResult = await processImport(importFile!, priceFieldValue!, existingId, baseCustomerTypeId);
        const ctToSave = (importResult as any).customerType as CustomerType;

        const finalCt = {
            ...currentCustomerType,
            ...ctToSave,
            baseCustomerTypeId: baseCustomerTypeId || currentCustomerType?.baseCustomerTypeId
        };

        const importedName = ctToSave?.name;

        if (existingId && currentCustomerType) {
            if (importedName !== currentCustomerType.name) {
                setPendingImportData({
                    result: importResult,
                    duplicatedId: existingId,
                    name: importedName
                });
                setPendingRenameData(ctToSave);
                openModal(ModalId.RENAME_CUSTOMER_TYPE);
                return;
            }
        }

        if (!existingId) {
            const duplicatedList = await checkNameExists(importedName);

            if (duplicatedList) {
                setPendingImportData({
                    result: importResult,
                    duplicatedId: duplicatedList.id,
                    name: importedName
                });
                openModal(ModalId.OVERWRITE_CUSTOMER_TYPE);
                return;
            }
        }
        await persist(finalCt, existingId);

        setResult(importResult);
        setStep(6);
    };

    const onConfirmOverwrite = async () => {
        if (pendingImportData) {
            const ctToSave = (pendingImportData.result as any).customerType as CustomerType;
            await persist(ctToSave, pendingImportData.duplicatedId);
            setResult(pendingImportData.result);
            setPendingImportData(null);
            setStep(6);
        }
    };

    const onNewUpdate = () => {
        setPriceFieldValue(undefined)
        setImportFile(undefined)
        setResult(undefined)
        setStep(1)
    }

    return (
        <PanelLoader
            title="Importar Lista de Precios"
            crumbs={crumbs}
            actions={actions}
            ready
        >
            <>
                {step !== 6 ? (
                    <>
                        {renderStep(
                            1,
                            'Seleccionar Lista Base',
                            <FieldList fields={baseListField} />
                        )}

                        {step > 1 && renderStep(
                            2,
                            'Seleccionar el campo a actualizar.',
                            <FieldList fields={priceFields} />
                        )}

                        {step > 2&& renderStep(
                            3,
                            'Descargar la plantilla del archivo.',
                            <FieldList fields={toList(templateField)} />
                        )}

                        {step > 3 && renderStep(
                            4,
                            'Editar el archivo descargado con los nuevos valores e importarlo.',
                            <FieldList fields={toList(importField)} />
                        )}

                        {step > 4 && importField && renderStep(
                            5,
                            'Actualizar los precios con los valores importados.',
                            <Button
                                icon="repeat"
                                label="Importar Lista de Precios"
                                disabled={step !== 5}
                                onClick={onCreateCustomerType}
                            />
                        )}
                    </>
                ) : (
                    renderStep(
                        6,
                        // Sin filas con precio también es válido (por ejemplo, si solo cambió el ajuste general)
                        result!.importedRows.length > 0 || result!.unimportedRows.length === 0 ?
                            (existingId ? 'La lista de precios se actualizó correctamente.' : 'La lista de precios se creó correctamente.') :
                            'No se pudieron procesar los cambios.',
                        <ImportedCustomerTypeReport
                            csvImportResult={result!}
                            onNewUpdate={onNewUpdate}
                            listName={currentCustomerType?.name}
                        />
                    )
                )}
                <Modal
                    modalId={ModalId.OVERWRITE_CUSTOMER_TYPE}
                    title="Lista ya existente"
                    submitLabel="Sobreescribir"
                    cancelLabel="Cancelar"
                    onCancel={() => setPendingImportData(null)}
                    onSubmit={onConfirmOverwrite}
                >
                    <p>
                        Ya existe una lista llamada <strong>{pendingImportData?.name}</strong>.
                        ¿Deseas sobreescribir los valores?
                    </p>
                </Modal>
                <Modal
                    modalId={ModalId.RENAME_CUSTOMER_TYPE}
                    title="Conflicto de Nombres"
                    cancelLabel='Cancelar Edición'
                    onCancel={() => {
                        onNewUpdate();
                        setPendingRenameData(null);
                        setPendingImportData(null);
                        goToCustomerTypes();
                    }}
                >
                    <div>
                        <p>El nombre de la lista del archivo (<strong>{pendingRenameData?.name}</strong>) no coincide con la lista que se está editando (<strong>{currentCustomerType?.name}</strong>).</p>
                        <p>¿Qué deseas hacer?</p>

                        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginTop: '24px' }}>
                            <Button
                                label={`Actualizar precios y cambiar nombre a "${pendingRenameData?.name}"`}
                                kind="primary"
                                onClick={async () => {
                                    if (pendingRenameData && pendingImportData?.result) {
                                        closeModal(ModalId.RENAME_CUSTOMER_TYPE);
                                        await persist(pendingRenameData, existingId);
                                        setResult(pendingImportData.result);
                                        setPendingRenameData(null);
                                        setPendingImportData(null);
                                        setStep(6);
                                    }
                                }}
                            />
                            <Button
                                label={`Actualizar precios y mantener nombre "${currentCustomerType?.name}"`}
                                kind="senary"
                                onClick={async () => {
                                    if (pendingRenameData && currentCustomerType && pendingImportData?.result) {
                                        closeModal(ModalId.RENAME_CUSTOMER_TYPE);
                                        const fixedCt = { ...pendingRenameData, name: currentCustomerType.name };
                                        await persist(fixedCt, existingId);
                                        setResult(pendingImportData.result);
                                        setPendingRenameData(null);
                                        setPendingImportData(null);
                                        setStep(6);
                                    }
                                }}
                            />
                        </div>
                    </div>
                </Modal>
            </>
        </PanelLoader>
    )
}
